import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import {
  getIssuesSavedQueries,
  getIssuesSavedQueriesByProject,
  getSupersetToken,
  getTimesheetSavedQueries,
  getAgileSavedQueriesByProject,
  getConfirmationsSavedQueries,
  getProjectsSavedQueries,
  getArtSavedQueries,
} from '../../../../packages/common/api';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteFileIcon from '../../../../packages/common/shared-ui/src/icons/DeleteFileIcon';
import { generateRedirectUrl } from '../../../../packages/common/utils/getRedirectUrl';
import { getTranslatedText } from '../../../../packages/common/utils/getTranslatedText';
import { store } from '../../app/redux';
import { toggleDrawer } from '../../app/redux/reducers/userSlice';

// Original class: menuItem
const StyledMenuItem = styled(Box)(theme => ({
  transition: '.4s ease',
  minHeight: '32px',
  padding: '4px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: '#E1E4FA',
  },
}));
// Original class: menuItemIcon
const StyledMenuItemIcon = styled(ChevronIcon)(theme => ({
  height: '24px!important',
  margin: '0 4px',
  cursor: 'pointer',
}));
// Original class: menuSubItems
const StyledMenuSubItems = styled(Popover)(theme => ({
  '&.MuiPopover-root': {
    zIndex: 2000,
    width: 300,
  },
}));
// Original class: menuSubItemsPaper
const StyledMenuSubItemsPaper = styled(Paper)(({ theme }) => ({
  '&.MuiPaper-root': {
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    maxWidth: 305,
    width: '100%',
    minHeight: 32,
    borderRadius: theme.spacing(0, 2, 2, 0),
    boxShadow: '0px 5px 10px -10px #212346',
  },
}));
const MenuItem = ({ item, context }) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [currentItem, setCurrentItem] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [disableSubitems, setDisableSubitems] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const redmineToken = useSelector(state => state.user.redmine_access_token);
  const state = store.getState();
  const dispatch = useDispatch();
  const pageParams = useParams();

  const closeSubItemsMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenSubMenu(false);
    //setDropdownListStatus(false);
  }, []);

  const MenuItemsEnum = {
    PROJECTS: 'projectsNavItem',
    ISSUES: 'issuesNavItem',
    PROJECT_ISSUES: 'projectIssuesNavItem',
    CONFIRMATIONS: 'confirmationsNavItem',
    PROJECT_CONFIRMATIONS: 'projectConfirmationsNavItem',
    TIMESHEETS: 'timesheetsNavItem',
    FINANCE: 'financeNavItem',
    TIMESHEET: 'timesheetTableNavItem',
    RESOURCE_PLAN: 'resourcePlanNavItem',
    ISSUES_BOARD: 'issuesBoardNavItem',
    ADMIN: 'adminMainNavItem',
    ADMIN_USERS: 'adminUsersNavItem',
    ADMIN_REPORTS: 'adminReportsNavItem',
    ADMIN_CALENDAR: 'adminCalendarNavItem',
    ADMIN_RESOURCE_PLAN: 'adminResourcePlanNavItem',
    ADMIN_CONFIRMATIONS: 'adminConfirmationsNavItem',
    ADMIN_CALC_TYPES: 'adminCalcTypesNaveItem',
    ADMIN_CONTRACT_STAGES: 'adminContractStagesNavItem',
    ADMIN_GANTT: 'adminGanttNavItem',
    ADMIN_COMPETENCY: 'adminCompetencyMatrix',
    ADMIN_DEPARTMENTS: 'adminDepartmentsNavItem',
    PROJECT_GANTT: 'projectGanttNavItem',
    ADMIN_FINANCE: 'adminFinanceNavItem',
    ADMIN_DATA_IMPORT: 'adminDataImportNavItem',
    ISSUES_KANBAN: 'issuesKanbanNavItem',
    PROJECT_ISSUES_KANBAN: 'projectIssuesKanbanNavItem',
    PROJECT_TIMESHEETS: 'projectTimesheetsNavItem',
    PROJECT_REPORTS: 'projectReportsNavItem',
    PROJECT_SPRINT_MANAGEMENT: 'projectSprintManagementNavItem',
    PROJECT_ISSUES_BOARD: 'projectIssuesBoardNavItem',
    ADMIN_OBJECT_HIERARCHY: 'adminObjectHierarchyNavItem',
    ADMIN_TIMESHEETS: 'adminTimesheetsNavItem',
  };

  const menuItemsData = useRef(
    new Map([
      [
        MenuItemsEnum.PROJECTS,
        { link: '/projects', getResponse: getProjectsSavedQueries },
      ],
      [
        MenuItemsEnum.ISSUES,
        { link: '/issues', getResponse: getIssuesSavedQueries },
      ],
      [
        MenuItemsEnum.PROJECT_ISSUES,
        { link: '/issues', getResponse: getIssuesSavedQueries },
      ],
      [
        MenuItemsEnum.CONFIRMATIONS,
        { link: '/confirmations', getResponse: getConfirmationsSavedQueries },
      ],
      [
        MenuItemsEnum.PROJECT_CONFIRMATIONS,
        { link: '/confirmations', getResponse: getConfirmationsSavedQueries },
      ],
      [
        MenuItemsEnum.TIMESHEETS,
        { link: '/timesheets', getResponse: getTimesheetSavedQueries },
      ],
      [
        MenuItemsEnum.FINANCE,
        { link: '/finances', getResponse: getArtSavedQueries },
      ],
      [MenuItemsEnum.TIMESHEET, { link: '/spreadsheet' }],
      [
        MenuItemsEnum.PROJECT_TIMESHEETS,
        { link: '/timesheets', getResponse: getTimesheetSavedQueries },
      ],
      [MenuItemsEnum.RESOURCE_PLAN, { link: '/resourcePlan' }],
      [MenuItemsEnum.ISSUES_BOARD, { link: '', externalLink: '/agile/board' }],
      [
        MenuItemsEnum.PROJECT_ISSUES_BOARD,
        {
          link: '',
          externalLink: `/projects/${state.user.project?.identifier}/agile/board`,
        },
      ],
      [
        MenuItemsEnum.ADMIN,
        {
          link: `${window.APP_CONFIG.env.REDMINE_HOST}/admin`,
          externalLink: '/admin',
        },
      ],
      [MenuItemsEnum.PROJECT_GANTT, { link: '/gantt' }],
      [
        MenuItemsEnum.PROJECT_ISSUES_KANBAN,
        { link: '/agile', getResponse: getAgileSavedQueriesByProject },
      ],
      [
        MenuItemsEnum.ISSUES_KANBAN,
        { link: '/agile', getResponse: getAgileSavedQueriesByProject },
      ],
      [
        MenuItemsEnum.PROJECT_SPRINT_MANAGEMENT,
        {
          link: '',
          externalLink: `/projects/${state.user.project?.identifier}/settings?tab=agile_sprints&X-Lance-Skip-Redirection=true`,
        },
      ],
      [MenuItemsEnum.ADMIN_USERS, { link: '/administration/users' }],
      [
        MenuItemsEnum.ADMIN_REPORTS,
        { link: '/administration/project-reports' },
      ],
      [
        MenuItemsEnum.ADMIN_CALENDAR,
        {
          link: `/administration/production-calendar?year=${dayjs().format('YYYY')}`,
        },
      ],
      [
        MenuItemsEnum.ADMIN_RESOURCE_PLAN,
        { link: '/administration/resourcePlan-settings' },
      ],
      [
        MenuItemsEnum.ADMIN_CONFIRMATIONS,
        { link: '/administration/confirmation-settings' },
      ],
      [
        MenuItemsEnum.ADMIN_CALC_TYPES,
        { link: '/administration/calculation-types' },
      ],
      [
        MenuItemsEnum.ADMIN_CONTRACT_STAGES,
        { link: '/administration/contract-stages' },
      ],
      [MenuItemsEnum.ADMIN_GANTT, { link: '/administration/gantt-settings' }],
      [MenuItemsEnum.ADMIN_FINANCE, { link: '/administration/art-settings' }],
      [MenuItemsEnum.ADMIN_DATA_IMPORT, { link: '/administration/import' }],
      [MenuItemsEnum.ADMIN_COMPETENCY, { link: '/administration/competency' }],
      [
        MenuItemsEnum.ADMIN_DEPARTMENTS,
        { link: '/administration/departments' },
      ],
      [
        MenuItemsEnum.ADMIN_OBJECT_HIERARCHY,
        { link: '/administration/objects-hierarchy' },
      ],
      [
        MenuItemsEnum.ADMIN_TIMESHEETS,
        { link: '/administration/timesheets-settings' },
      ],
    ]),
  );

  useEffect(() => {
    if (item) {
      setCurrentItem(item);
    }
  }, [item]);

  const handleOpenSubMenu = useCallback(
    async e => {
      setAnchorEl(e.currentTarget);
      let updatedObject = {};
      if (currentItem.itemId === MenuItemsEnum.PROJECT_REPORTS) {
        setItemsLoading(true);
        //const supersetToken = await getSupersetToken();
        if (state.user.project?.reports.length > 0) {
          updatedObject = {
            ...currentItem,
            subitems: state.user.project?.reports.map(item => {
              const reportUrl = new URL(item.url);
              //reportUrl.searchParams.append('token', supersetToken.data);
              return {
                itemId: item.id,
                itemName: item.name,
                query: true,
                path: reportUrl,
              };
            }),
          };
          setCurrentItem(updatedObject);
          setItemsLoading(false);
          setOpenSubMenu(true);
        }
      } else {
        if (menuItemsData.current.get(currentItem.itemId).getResponse) {
          setItemsLoading(true);
          try {
            const projectQuery = [
              MenuItemsEnum.PROJECT_ISSUES,
              MenuItemsEnum.FINANCE,
              MenuItemsEnum.PROJECT_TIMESHEETS,
              MenuItemsEnum.PROJECT_ISSUES_KANBAN,
              MenuItemsEnum.PROJECT_CONFIRMATIONS,
            ];
            let params = {};
            projectQuery.includes(currentItem.itemId) &&
              (params.project = pageParams.projectId);
            const { data: response } = await menuItemsData.current
              .get(currentItem.itemId)
              .getResponse(params);
            if (response) {
              if (response.length > 0) {
                updatedObject = {
                  ...currentItem,
                  subitems: response.map(item => {
                    return {
                      itemId: item.id,
                      itemName: item.name,
                      query: true,
                    };
                  }),
                };
                setCurrentItem(updatedObject);
                setItemsLoading(false);
                setOpenSubMenu(true);
                setDisableSubitems(false);
              } else {
                setDisableSubitems(true);
                setItemsLoading(false);
              }
            }
          } catch (error) {
            console.error('ERROR WITH GET', error);
          }
        } else {
          setAnchorEl(e.currentTarget);
          setOpenSubMenu(true);
        }
      }
    },
    [
      MenuItemsEnum.PROJECT_ISSUES,
      MenuItemsEnum.FINANCE,
      MenuItemsEnum.PROJECT_ISSUES_KANBAN,
      MenuItemsEnum.PROJECT_REPORTS,
      MenuItemsEnum.PROJECT_TIMESHEETS,
      MenuItemsEnum.PROJECT_CONFIRMATIONS,
      currentItem,
      pageParams.projectId,
      state.user.project?.reports,
    ],
  );

  const redirectToAnotherUrl = useCallback(
    async menuItem => {
      const isDev = process.env.ENV === 'dev';
      if (menuItem.query) {
        if (menuItem.path) {
          window.open(menuItem.path, '_blank');
        } else {
          let link = `${menuItemsData.current.get(currentItem.itemId).link}/query/${menuItem.itemId}`;
          menuItemsData.current.get(currentItem.itemId).link !== '/projects' &&
            pageParams.projectId &&
            (link = `/projects/${pageParams.projectId}${menuItemsData.current.get(currentItem.itemId).link}/query/${
              menuItem.itemId
            }`);
          window.open(link, '_self');
        }
        dispatch(toggleDrawer(false));
      } else {
        const hasExternalLink = !!menuItemsData.current.get(menuItem.itemId)
          .externalLink;
        if (!isDev && hasExternalLink) {
          const link = generateRedirectUrl({
            host: window.location.origin,
            path: menuItemsData.current.get(menuItem.itemId).externalLink,
            redmineToken,
          });
          dispatch(toggleDrawer(false));
          window.open(link);
        } else {
          let link = menuItemsData.current.get(menuItem.itemId).link;
          if (menuItem.context) {
            link = `/projects/${state.user.project.redmineId}${link}`;
          }
          dispatch(toggleDrawer(false));
          window.open(link, '_self');
        }
      }
    },

    [currentItem, navigate, redmineToken, pageParams.projectId],
  );

  return (
    <>
      {currentItem && (
        <StyledMenuItem
          sx={{
            paddingLeft: context ? '80px' : '60px',
          }}
        >
          <Box
            component={'span'}
            onClick={() => redirectToAnotherUrl(currentItem)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography
              variant='h3'
              sx={{
                fontWeight: 600,
                color: '#212346',
              }}
            >
              {currentItem.itemName}
            </Typography>
          </Box>

          {currentItem.subitems && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {itemsLoading && (
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  <CircularProgress size={20} color='secondary' />
                </Grid>
              )}
              <IconButton
                disabled={disableSubitems}
                sx={{ padding: '0', margin: '0 4px 0 0 !important' }}
                onClick={event => handleOpenSubMenu(event)}
              >
                <StyledMenuItemIcon direction={'right'} viewBox='0 0 24 24' />
              </IconButton>
            </Box>
          )}
          {currentItem.subitems && (
            <StyledMenuSubItems
              open={openSubMenu}
              anchorEl={anchorEl}
              onClose={closeSubItemsMenu}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                paper: {
                  component: StyledMenuSubItemsPaper,
                },
              }}
            >
              {currentItem.subitems.map((item, index) => (
                <StyledMenuItem
                  key={index}
                  sx={{
                    paddingLeft: '36px',
                  }}
                >
                  <Box
                    component={'span'}
                    onClick={() => redirectToAnotherUrl(item)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        color: '#212346',
                      }}
                    >
                      {item.itemName}
                    </Typography>
                  </Box>
                </StyledMenuItem>
              ))}
            </StyledMenuSubItems>
          )}
        </StyledMenuItem>
      )}
    </>
  );
};

export default MenuItem;
